<template>
  <div class="rounded-xl px-4 md:px-2 py-6 mb-6">
    <div class="flex flex-col lg:flex-row gap-4 lg:gap-6 lg:items-start">
      <div class="flex flex-col gap-3">
        <h3 class="text-sm font-semibold text-on-background">Channels</h3>
        <div class="flex gap-2 flex-wrap items-center">
          <Chip
            v-for="channelOption in channelOptions"
            :key="channelOption.value"
            class="cursor-pointer"
            :size="ChipSize.LG"
            :color="ChipColor.SECONDARY"
            :variant="
              channelOption.value === pageContext.channelFilters.activeChannel
                ? ChipVariant.FILLED
                : ChipVariant.OUTLINE
            "
            @click="handleChannelChange(channelOption.value)"
          >
            <span class="px-2">{{ channelOption.label }}</span>
          </Chip>
        </div>
      </div>

      <div class="hidden lg:block w-px h-16 bg-line mt-6"></div>

      <div class="flex flex-col gap-3">
        <div class="flex gap-3">
          <div class="flex flex-col gap-3">
            <label class="text-sm font-semibold text-on-background"
              >Start Date</label
            >
            <date-input
              v-model="startDateInput"
              placeholder="Select date"
              clearable
              class="w-36 date-input-align"
            />
          </div>
          <div class="flex flex-col gap-3">
            <label class="text-sm font-semibold text-on-background"
              >End Date</label
            >
            <date-input
              v-model="endDateInput"
              placeholder="Select date"
              clearable
              class="w-36 date-input-align"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, Ref } from 'vue';
import { parse } from 'date-fns';
import { DateInput } from '~/components/inputs/date-input';
import { Chip, ChipColor, ChipSize, ChipVariant } from '~/components/chip';
import { useProvidedContext } from '@/composables/use-provided-context';
import { PROMOTE_PAGE_INJECT, CHANNEL_LABELS } from '../../_constants';
import { PromotePageContext, Channel } from '../../_types';

const emit = defineEmits<{
  (e: 'filtersChanged'): void;
}>();

const pageContext =
  useProvidedContext<Ref<PromotePageContext>>(PROMOTE_PAGE_INJECT);

const startDateInput = ref<string>(
  pageContext.value.channelFilters.startDate || '',
);
const endDateInput = ref<string>(
  pageContext.value.channelFilters.endDate || '',
);

const channelOptions = computed(() => {
  const options: Array<{ value: Channel | 'all'; label: string }> = [
    { value: 'all' as const, label: 'All Channels' },
  ];

  for (const channel of pageContext.value.connectedChannels.channels) {
    if (pageContext.value.connectedChannels.hasData[channel]) {
      options.push({
        value: channel,
        label: CHANNEL_LABELS[channel],
      });
    }
  }

  return options;
});

const formatDateToISO = (date: string): string | null => {
  if (!date) return null;
  const parsedDate = parse(date, 'MM/dd/yy', new Date());

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const [isoDate] = parsedDate.toISOString().split('T');
  return isoDate;
};

const handleChannelChange = (channel: Channel | 'all') => {
  pageContext.value.channelFilters.activeChannel = channel;

  pageContext.value.channelDetails.page = 1;
  emit('filtersChanged');
};

const updateDateFilters = () => {
  pageContext.value.channelFilters.startDate = formatDateToISO(
    startDateInput.value,
  );
  pageContext.value.channelFilters.endDate = formatDateToISO(
    endDateInput.value,
  );

  pageContext.value.channelDetails.page = 1;
  emit('filtersChanged');
};

const clearDateRange = () => {
  startDateInput.value = '';
  endDateInput.value = '';
  updateDateFilters();
};

const clearStartDate = () => {
  startDateInput.value = '';
  updateDateFilters();
};

const clearEndDate = () => {
  endDateInput.value = '';
  updateDateFilters();
};

watch(startDateInput, updateDateFilters);
watch(endDateInput, updateDateFilters);
</script>

<style scoped>
:deep(.date-input-align) {
  display: flex;
  align-items: center;
}

:deep(.date-input-align .input-area) {
  margin: 0 !important;
  align-self: center;
}
</style>
